import { useRef, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Eraser, PenLine, Type } from "lucide-react";

export function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [mode, setMode] = useState("draw");
  const [typed, setTyped] = useState("");

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = c.offsetWidth * 2;
    c.height = c.offsetHeight * 2;
    const ctx = c.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1A1A1A";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  const clearCanvas = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
  };

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => { if (mode !== "draw") return; e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(canvasRef.current.toDataURL("image/png")); };

  const clear = () => { clearCanvas(); setTyped(""); onChange(""); };

  const applyTyped = (val) => {
    setTyped(val);
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    clearCanvas();
    ctx.fillStyle = "#1A1A1A";
    ctx.font = "44px 'Cormorant Garamond', serif";
    ctx.textBaseline = "middle";
    ctx.fillText(val, 20, c.offsetHeight / 2);
    onChange(val ? c.toDataURL("image/png") : "");
  };

  const switchMode = (m) => { setMode(m); clear(); };

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <button type="button" onClick={() => switchMode("draw")} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${mode === "draw" ? "bg-ink text-white border-ink" : "border-line"}`} data-testid="sig-mode-draw"><PenLine className="h-3 w-3 inline mr-1" />Draw</button>
        <button type="button" onClick={() => switchMode("type")} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${mode === "type" ? "bg-ink text-white border-ink" : "border-line"}`} data-testid="sig-mode-type"><Type className="h-3 w-3 inline mr-1" />Type</button>
        <button type="button" onClick={clear} className="text-xs px-3 py-1.5 rounded-full border border-line ml-auto" data-testid="sig-clear"><Eraser className="h-3 w-3 inline mr-1" />Clear</button>
      </div>
      {mode === "type" && <Input value={typed} onChange={(e) => applyTyped(e.target.value)} placeholder="Type your signature" className="df-input mb-2" data-testid="sig-type-input" />}
      <canvas
        ref={canvasRef}
        className="w-full h-40 rounded-xl border border-line bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      {mode === "draw" && <p className="text-[11px] text-muted-foreground mt-1">Draw your signature above with your mouse or finger.</p>}
    </div>
  );
}

export default SignaturePad;
